/**
 * React Query hooks for admin API
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import * as adminApi from '../services/adminApi'
import type {
  EntityType,
  ClassificationType,
  StatsOverview,
  EntityListResponse,
  FileUploadResponse,
  EditionsListResponse,
  ImportExecuteParams,
  ClassificationListItem,
  DataSourcesListResponse,
  DataSourceDetailResponse,
  DataSourceField,
  FieldLabelUpdateRequest,
  FieldLabelResponse,
  EntityLevel,
} from '../types'

// ============================================================================
// Stats
// ============================================================================

export function useStatsOverview() {
  return useQuery<StatsOverview>({
    queryKey: ['admin', 'stats', 'overview'],
    queryFn: adminApi.getStatsOverview,
    staleTime: 30 * 1000, // 30 seconds
  })
}

export function useEditionStats() {
  return useQuery<adminApi.EditionStatsResponse>({
    queryKey: ['admin', 'stats', 'editions'],
    queryFn: adminApi.getEditionStats,
    staleTime: 30 * 1000, // 30 seconds
  })
}

export function useClassificationStats() {
  return useQuery<adminApi.ClassificationStatsResponse>({
    queryKey: ['admin', 'stats', 'classifications'],
    queryFn: adminApi.getClassificationStats,
    staleTime: 60 * 1000, // 1 minute
  })
}

// ============================================================================
// Entity Lists
// ============================================================================

export function useEntityList<T = Record<string, unknown>>(
  entityType: EntityType,
  { limit = 50, offset = 0, search = '', editionId = null }: { limit?: number; offset?: number; search?: string; editionId?: number | null } = {}
) {
  return useQuery<EntityListResponse<T>>({
    queryKey: ['admin', 'entities', entityType, { limit, offset, search, editionId }],
    queryFn: () => adminApi.getEntityList<T>({ entityType, limit, offset, search, editionId }),
    staleTime: 60 * 1000, // 1 minute
  })
}

// ============================================================================
// Editions
// ============================================================================

export function useEditions(dataset?: string) {
  return useQuery<EditionsListResponse>({
    queryKey: ['admin', 'editions', dataset],
    queryFn: () => adminApi.getEditions(dataset),
    staleTime: 30 * 1000, // 30 seconds
  })
}

// ============================================================================
// Import
// ============================================================================

export function useUploadGV100() {
  return useMutation<FileUploadResponse, Error, { adFile: File; nadFile?: File }>({
    mutationFn: ({ adFile, nadFile }) => adminApi.uploadGV100Files(adFile, nadFile),
  })
}

export function useExecuteImport() {
  const queryClient = useQueryClient()

  return useMutation<adminApi.ExecuteImportResponse, Error, { uploadId: string; params: ImportExecuteParams }>({
    mutationFn: ({ uploadId, params }) => adminApi.executeGV100Import(uploadId, params),
    onSuccess: (data) => {
      // Only invalidate if import completed directly (sync mode)
      if (data.status === 'completed') {
        queryClient.invalidateQueries({ queryKey: ['admin', 'entities'] })
        queryClient.invalidateQueries({ queryKey: ['admin', 'stats'] })
        queryClient.invalidateQueries({ queryKey: ['admin', 'editions'] })
      }
    },
  })
}

export function useImportAll() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: adminApi.importAll,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin'] })
    },
  })
}

// ============================================================================
// Classifications (NAD)
// ============================================================================

export function useClassificationList(
  classificationType: ClassificationType,
  { limit = 50, offset = 0, search = '' }: { limit?: number; offset?: number; search?: string } = {}
) {
  return useQuery<EntityListResponse<ClassificationListItem>>({
    queryKey: ['admin', 'classifications', classificationType, { limit, offset, search }],
    queryFn: () => adminApi.getClassificationList({ classificationType, limit, offset, search }),
    staleTime: 60 * 1000, // 1 minute
  })
}

export function useUpdateClassification(classificationType: ClassificationType) {
  const queryClient = useQueryClient()

  return useMutation<
    adminApi.ClassificationUpdateResponse,
    Error,
    { itemId: number; data: adminApi.ClassificationUpdateData }
  >({
    mutationFn: ({ itemId, data }) =>
      adminApi.updateClassificationItem(classificationType, itemId, data),
    onSuccess: () => {
      // Invalidate the classification list to refetch updated data
      queryClient.invalidateQueries({
        queryKey: ['admin', 'classifications', classificationType],
      })
    },
  })
}

export function useImportClassificationCsv(classificationType: ClassificationType) {
  const queryClient = useQueryClient()

  return useMutation<adminApi.ClassificationImportResponse, Error, File>({
    mutationFn: (file) => adminApi.importClassificationCsv(classificationType, file),
    onSuccess: () => {
      // Invalidate the classification list to refetch updated data
      queryClient.invalidateQueries({
        queryKey: ['admin', 'classifications', classificationType],
      })
    },
  })
}

// ============================================================================
// Data Sources
// ============================================================================

export function useDataSources(entityLevel?: EntityLevel) {
  return useQuery<DataSourcesListResponse>({
    queryKey: ['admin', 'data-sources', entityLevel],
    queryFn: () => adminApi.getDataSources(entityLevel),
    staleTime: 60 * 1000, // 1 minute
  })
}

export function useDataSourceDetail(sourceTable: string, enabled = true) {
  return useQuery<DataSourceDetailResponse>({
    queryKey: ['admin', 'data-sources', sourceTable],
    queryFn: () => adminApi.getDataSourceDetail(sourceTable),
    staleTime: 60 * 1000,
    enabled,
  })
}

export function useFieldLabels(sourceTable: string, enabled = true) {
  return useQuery<DataSourceField[]>({
    queryKey: ['admin', 'field-labels', sourceTable],
    queryFn: () => adminApi.getFieldLabels(sourceTable),
    staleTime: 60 * 1000,
    enabled,
  })
}

export function useUpdateFieldLabel(sourceTable: string) {
  const queryClient = useQueryClient()

  return useMutation<FieldLabelResponse, Error, { labelId: number; data: FieldLabelUpdateRequest }>({
    mutationFn: ({ labelId, data }) => adminApi.updateFieldLabel(labelId, data),
    onSuccess: () => {
      // Invalidate both field labels and data source detail
      queryClient.invalidateQueries({
        queryKey: ['admin', 'field-labels', sourceTable],
      })
      queryClient.invalidateQueries({
        queryKey: ['admin', 'data-sources', sourceTable],
      })
    },
  })
}

export function useResetFieldLabels(sourceTable: string) {
  const queryClient = useQueryClient()

  return useMutation<{ reset_count: number }, Error>({
    mutationFn: () => adminApi.resetFieldLabels(sourceTable),
    onSuccess: () => {
      // Invalidate both field labels and data source detail
      queryClient.invalidateQueries({
        queryKey: ['admin', 'field-labels', sourceTable],
      })
      queryClient.invalidateQueries({
        queryKey: ['admin', 'data-sources', sourceTable],
      })
    },
  })
}

// ============================================================================
// Reference Data (Regionalisierung & Typisierung)
// ============================================================================

export function useReferenceDataStatus() {
  return useQuery<adminApi.ReferenceDataStatusResponse>({
    queryKey: ['admin', 'reference-data', 'status'],
    queryFn: adminApi.getReferenceDataStatus,
    staleTime: 60 * 1000, // 1 minute
  })
}

export function useLoadReferenceData() {
  const queryClient = useQueryClient()

  return useMutation<adminApi.ReferenceDataLoadResponse, Error, string[] | undefined>({
    mutationFn: (tables) => adminApi.loadReferenceData(tables),
    onSuccess: () => {
      // Invalidate reference data status and stats
      queryClient.invalidateQueries({
        queryKey: ['admin', 'reference-data'],
      })
      queryClient.invalidateQueries({
        queryKey: ['admin', 'stats'],
      })
      queryClient.invalidateQueries({
        queryKey: ['admin', 'classifications'],
      })
    },
  })
}
